import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'components/hooks/useToast';
import produce from 'immer';
import { findIndex } from 'lodash';
import { oneOneOneReactQueryKeys } from 'screens/oneOnOneRabbit/InstancePage/constants/oneOnOneReactQueryKeys';
import { OneOnOneAgenda } from 'screens/oneOnOneRabbit/interfaces/OneOnOneAgenda';
import { OneOnOneNotes } from 'screens/oneOnOneRabbit/interfaces/OneOnOneNotes';
import { OneOnOneRequestI } from 'screens/oneOnOneRabbit/interfaces/common';
import { z } from 'zod';

import { reorderOneOnOneAgenda } from '../api';
import {
  createOneOnOneAgenda,
  deleteOneOnOneAgenda,
  getOneOnOneAgenda,
  getOneOnOneBotRecommendedData,
  RequireAtLeastOne,
  updateOneOnOneAgenda,
  updateOneOnOneAgendaCompletion,
  updateOneOnOneAgendaNotes,
} from '../services';

export type OneOnOneAgendaCreateOrUpdateI = z.infer<typeof OneOnOneAgenda>;

export type OneOnOneAgendaNotesI = z.infer<typeof OneOnOneNotes>;

export const useOneOnOneAgendaQuery = ({ masterId, instanceId }: OneOnOneRequestI) =>
  useQuery(
    [oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId],
    () => getOneOnOneAgenda({ masterId, instanceId })
    // {
    //   enabled: !!masterId
    // }
  );

export const useCreateAgendaMutation = ({ masterId, instanceId }: OneOnOneRequestI) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ updatedAgenda }: { updatedAgenda: OneOnOneAgendaCreateOrUpdateI; onMutation: () => void }) =>
      await createOneOnOneAgenda({ masterId, instanceId, ...updatedAgenda }),
    onMutate: async ({
      updatedAgenda,
      onMutation,
    }: {
      updatedAgenda: OneOnOneAgendaCreateOrUpdateI;
      onMutation: () => void;
    }) => {
      await queryClient.cancelQueries([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId]);

      const previousAgenda = queryClient.getQueryData([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId]);

      queryClient.setQueryData(
        [oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId],
        (old: OneOnOneAgendaCreateOrUpdateI[]) => [...old, updatedAgenda]
      );

      onMutation();

      return { previousAgenda };
    },
    onError: (err: { message: 'string' }, newAgenda, context) => {
      toast({
        title: err?.message || 'An unknown error has occurred, please try again',
        status: 'error',
      });
      queryClient.setQueryData([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId], context.previousAgenda);
    },
    onSuccess: (data) => {
      toast({
        title: 'Agenda has been created',
      });
      //  doing not so ideal stuff, because doing a refetch is not preferred by the team
      queryClient.setQueryData(
        [oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId],
        (old: OneOnOneAgendaCreateOrUpdateI[]) => {
          const newAgendaList = produce(old, (draft) => {
            const modifiedAgenda = draft[draft.length - 1];
            modifiedAgenda.instanceDataId = data.id;
            draft[draft.length - 1] = modifiedAgenda;
          });
          return newAgendaList;
        }
      );
    },
    onSettled: () => {
      // queryClient.invalidateQueries([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId]);
    },
  });
};

export const useUpdateAgendaMutation = ({ masterId, instanceId }: OneOnOneRequestI) => {
  const queryClient = useQueryClient();
  const toast = useToast();

  return useMutation({
    mutationFn: async ({ updatedAgenda }: { updatedAgenda: OneOnOneAgendaCreateOrUpdateI }) =>
      await updateOneOnOneAgenda({
        masterId,
        instanceId,
        instanceDataId: updatedAgenda.instanceDataId,
        templateAgendaId: updatedAgenda.templateAgendaId,
        ...updatedAgenda,
      }),
    onMutate: async ({ updatedAgenda }: { updatedAgenda: OneOnOneAgendaCreateOrUpdateI }) => {
      await queryClient.cancelQueries([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId]);

      const previousAgenda = queryClient.getQueryData([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId]);

      queryClient.setQueryData(
        [oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId],
        (old: OneOnOneAgendaCreateOrUpdateI[]) =>
          old.map((agenda) => {
            const idToSearch = updatedAgenda.instanceDataId ?? updatedAgenda.templateAgendaId;
            if (agenda.instanceDataId === idToSearch || agenda.templateAgendaId === idToSearch) {
              return updatedAgenda;
            }
            return agenda;
          })
      );

      return { previousAgenda };
    },
    onError: (err: { message?: 'string' }, newAgenda, context) => {
      toast({
        title: err?.message || 'An unknown error has occurred, please try again',
        status: 'error',
      });
      queryClient.setQueryData([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId], context.previousAgenda);
    },
    onSuccess: () => {
      toast({
        title: 'Agenda has been saved',
      });
    },
    onSettled: () => {
      // queryClient.invalidateQueries([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId]);
    },
  });
};

export const useDeleteAgendaMutation = ({ masterId, instanceId }: OneOnOneRequestI) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ instanceDataId }: { instanceDataId: number }) =>
      await deleteOneOnOneAgenda({
        masterId,
        instanceId,
        instanceDataId,
      }),
    onMutate: async ({ instanceDataId }: { instanceDataId: number }) => {
      await queryClient.cancelQueries([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId]);

      const previousAgenda = queryClient.getQueryData([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId]);

      queryClient.setQueryData(
        [oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId],
        (old: OneOnOneAgendaCreateOrUpdateI[]) => {
          const filteredArray = [...old];
          return filteredArray.filter((agenda) => agenda.instanceDataId !== instanceDataId);
        }
      );

      return { previousAgenda };
    },
    onError: (err, newAgenda, context) => {
      queryClient.setQueryData([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId], context.previousAgenda);
    },
    onSettled: () => {
      // queryClient.invalidateQueries([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId]);
    },
  });
};

type UpdateAgendaCompletionI = {
  isCompleted: boolean;
  instanceDataId?: number;
  templateAgendaId?: number;
};

export const useAgendaCompletionMutation = ({ masterId, instanceId }: OneOnOneRequestI) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ isCompleted, instanceDataId, templateAgendaId }: UpdateAgendaCompletionI) =>
      await updateOneOnOneAgendaCompletion({
        masterId,
        instanceId,
        instanceDataId,
        templateAgendaId,
        isCompleted,
      }),
    onMutate: async ({ isCompleted, instanceDataId, templateAgendaId }: UpdateAgendaCompletionI) => {
      await queryClient.cancelQueries([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId]);

      const previousAgenda = queryClient.getQueryData([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId]);

      queryClient.setQueryData(
        [oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId],
        (old: OneOnOneAgendaCreateOrUpdateI[]) =>
          old.map((agenda) => {
            const idToSearch = instanceDataId ?? templateAgendaId;
            if (agenda.instanceDataId === idToSearch || agenda.templateAgendaId === idToSearch) {
              return { ...agenda, isCompleted };
            }
            return agenda;
          })
      );

      return { previousAgenda };
    },
    onError: (err, newAgenda, context) => {
      queryClient.setQueryData([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId], context.previousAgenda);
    },
    onSettled: () => {
      // queryClient.invalidateQueries([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId]);
    },
  });
};

type OneOnOneAgendaNotesUpdateI = RequireAtLeastOne<
  {
    instanceDataId?: number;
    templateAgendaId?: number;
  },
  'instanceDataId' | 'templateAgendaId'
> &
  OneOnOneAgendaNotesI;

export const useUpdateAgendaNotesMutation = ({ masterId, instanceId }: OneOnOneRequestI) => {
  const queryClient = useQueryClient();
  const toast = useToast();
  return useMutation({
    mutationFn: async ({ agendaNote }: { agendaNote: OneOnOneAgendaNotesUpdateI }) =>
      await updateOneOnOneAgendaNotes({
        masterId,
        instanceId,
        instanceDataId: agendaNote.instanceDataId,
        templateAgendaId: agendaNote.templateAgendaId,
        ...agendaNote,
      }),
    onMutate: async ({ agendaNote }: { agendaNote: OneOnOneAgendaNotesUpdateI }) => {
      await queryClient.cancelQueries([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId]);

      const previousAgenda = queryClient.getQueryData([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId]);

      queryClient.setQueryData(
        [oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId],
        (old: OneOnOneAgendaCreateOrUpdateI[]) =>
          old.map((agenda) => {
            const idToSearch = agendaNote.instanceDataId ?? agendaNote.templateAgendaId;
            if (agenda.instanceDataId === idToSearch || agenda.templateAgendaId === idToSearch) {
              return {
                ...agenda,
                sharedNotes: agendaNote?.isPrivate ? agenda.sharedNotes : agendaNote,
                privateNotes: agendaNote?.isPrivate ? agendaNote : agenda.privateNotes,
              };
            }
            return agenda;
          })
      );

      return { previousAgenda };
    },
    onError: (err: { message: string }, newAgenda, context) => {
      toast({
        title: err?.message || 'An unknown error has occurred, please try again',
        status: 'error',
      });
      queryClient.setQueryData([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId], context.previousAgenda);
    },
    onSuccess: () => {
      toast({
        title: 'Agenda notes have been saved',
      });
    },
    onSettled: () => {
      // queryClient.invalidateQueries([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId]);
    },
  });
};

export type ReorderAgendaI = {
  reorderInstanceDataId: number | null;
  topInstanceDataId: number;
};

export const useReorderAgendaMutation = ({ masterId, instanceId }: OneOnOneRequestI) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ reorderInstanceDataId, topInstanceDataId }: ReorderAgendaI) =>
      await reorderOneOnOneAgenda({
        masterId,
        instanceId,
        reorderInstanceDataId,
        topInstanceDataId,
      }),
    onMutate: async ({ reorderInstanceDataId, topInstanceDataId }: ReorderAgendaI) => {
      await queryClient.cancelQueries([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId]);

      const previousAgenda = queryClient.getQueryData([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId]);

      queryClient.setQueryData(
        [oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId],
        (old: OneOnOneAgendaCreateOrUpdateI[]) => {
          const adminAddedAgenda = old.filter((agenda) => agenda.isOwnerAdmin);
          const userAddedAgenda = old
            .filter((agenda) => !agenda.isOwnerAdmin)
            .sort((a, b) => a.displayOrder - b.displayOrder);

          const reorderedAgenda = produce(userAddedAgenda, (draft) => {
            const agendaWithoutReorderingAgenda = draft.filter(
              (agenda) => agenda.instanceDataId !== reorderInstanceDataId
            );
            const reorderingAgenda = draft.find((agenda) => agenda.instanceDataId === reorderInstanceDataId);
            if (topInstanceDataId === null) {
              // if topInstanceDataId is null, then we are moving the agenda to the top
              const firstAgenda = { ...reorderingAgenda, displayOrder: 1 };
              const restOfAgenda = agendaWithoutReorderingAgenda.map((agenda) => ({
                ...agenda,
                displayOrder: agenda.displayOrder + 1,
              }));
              return [firstAgenda, ...restOfAgenda];
            }
            // if topInstanceDataId is not null, then we are moving the agenda to the bottom of the agenda with the topInstanceDataId
            const topAgenda = draft.find((agenda) => agenda.instanceDataId === topInstanceDataId);
            const topIndex = findIndex(
              agendaWithoutReorderingAgenda,
              (agenda) => agenda.instanceDataId === topInstanceDataId
            );
            const agendaBeingReordered = {
              ...reorderingAgenda,
              displayOrder: topAgenda.displayOrder + 1,
            };
            const restOfAgenda = agendaWithoutReorderingAgenda.map((agenda) => {
              if (agenda.displayOrder > topAgenda.displayOrder) {
                return { ...agenda, displayOrder: agenda.displayOrder + 2 };
              }
              return agenda;
            });

            restOfAgenda.splice(topIndex, 0, agendaBeingReordered);

            return restOfAgenda.sort((a, b) => a.displayOrder - b.displayOrder);
          });

          const updatedAgenda = [...adminAddedAgenda, ...reorderedAgenda];
          return updatedAgenda;
        }
      );

      return { previousAgenda };
    },
    onError: (err, newAgenda, context) => {
      queryClient.setQueryData([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId], context.previousAgenda);
    },
    onSettled: () => {
      // queryClient.invalidateQueries([oneOneOneReactQueryKeys.oneOnOneAgenda, masterId, instanceId]);
    },
  });
};

export const useOneOnOneBotRecommendedDataQuery = ({ masterId, instanceId }: OneOnOneRequestI) =>
  useQuery([oneOneOneReactQueryKeys.botRecommendedData, masterId, instanceId], () =>
    getOneOnOneBotRecommendedData({ masterId, instanceId })
  );
